<template>
  <patient-profile>
    <modal-dialog-prevent-leave
      v-bind:is-shown="isShownDialogLeaveConfirm"
      v-on:back="isShownDialogLeaveConfirm = false"
      v-on:leave="leaveWithoutSave"
      v-on:save="saveAndLeave" />
    <main class="patient-profile__questionnaire">
      <div class="patient-profile__questionnaire__header-buttons">
        <button
          v-if="isHasAddQuestion"
          class="patient-profile__questionnaire__button-create-new"
          type="button"
          v-on:click="onShowCreateModal"
        >
          Добавить вопрос
        </button>
        <button
          type="button"
          class="patient-profile__questionnaire__button-print"
          v-on:click="printQuestionnaire">
          Печать
        </button>
      </div>
      <iframe
        class="patient-profile__questionnaire__print"
        ref="printView"
        title="printView"
      />
      <form v-on:submit.prevent="storeQuestionnaire" class="patient-profile__questionnaire__form">
        <div class="patient-profile__questionnaire__units">
          <div
            v-for="questionsGroup in questionnaire.questions"
            :key="`group-${questionsGroup.id}`"
            class="patient-profile__questionnaire__unit"
          >
            <div class="patient-profile__questionnaire__caption">
              {{ questionsGroup.name }}
            </div>
            <draggable
              class="patient-questions patient-profile__questionnaire__questions"
              v-model="questionsGroup.questions"
              tag="ul"
              handle=".handle"
              :move="checkMove"
              ghostClass="ghost"
              @start="drag = true"
              @end="drag = false">
              <li
                v-for="question in questionsGroup.questions"
                :key="`question-${question.id}`"
                class="patient-questions__item"

              >
                <div class="patient-questions__question">
                  <span
                    class="patient-questions__drag-handle handle"
                    role="button"
                    tabindex="0"
                    aria-label="Перенести вопрос"
                    title="Зажмите и удерживайте для перемещения вопроса"
                  />
                  <checkbox-textarea
                    name-checkbox="checkbox-question"
                    name-text="input-question"
                    v-bind:max-length="maxLengthQuestion"
                    v-bind:disabled="!isHasEditQuestion"
                    v-bind:checked.sync="question.answer"
                    v-bind:title="removeLineBreaks(question.question)"
                    v-bind:input-errors="inputErrors[`${question.id}.length`]"
                    @blur="onSubmitEditPatientQuestion(
                      question.id,
                      question.question,
                      question.group_id,
                      question.is_open_question,
                      $event,
                    )"
                  />
                  <button
                    v-if="isHasDeleteQuestion"
                    type="button"
                    class="patient-questions__button-del"
                    title="Удалить вопрос"
                    v-on:click="showDeleteModal(question.id)"
                  />
                </div>
                <textarea-length-limitation
                  v-if="question.is_open_question"
                  class="patient-questions__description"
                  v-model="question.detailed_answer"
                  v-bind:max-length="1000"
                  v-on:text-availability="question.answer = $event"
                />
              </li>
            </draggable>

          </div>
        </div>
        <button
          type="button"
          v-bind:disabled="isQuestionnaireStoring"
          v-on:click="storeQuestionnaire"
          class="patient-profile__questionnaire__button"
        >
          Сохранить
        </button>
      </form>
      <side-slide-transition>
        <confirm-side-dialog
          label="Добавить вопрос"
          v-if="isShowCreateModal"
          v-on:cancel="isShowCreateModal = false"
        >
          <form-patient-health-question
            v-bind:patient-question="createPatientHealthQuestion"
            v-bind:input-errors="inputErrors"
            v-on:on-submit-question="onSubmitCreatePatientQuestion"
          />
        </confirm-side-dialog>
      </side-slide-transition>
      <modal-dialog
        v-model="deleteDialog"
        content-class="patient-questionnaire-modal"
        title="Вопрос будет удален в анкетах всех пациентов. Восстановить данные будет невозможно."
        title-center
        max-width="555px"
        with-cross
      >
        <div class="patient-questionnaire-modal__controls">
          <v-checkbox
            v-model="isAgreeWithRulesDelete"
            label="Ознакомлен и согласен с условиями удаления"
            hide-details />
          <button
            class="modal-button modal-button--remove"
            type="button"
            v-bind:disabled="!isAgreeWithRulesDelete"
            v-on:click="onDeleteQuestion">Удалить</button>
        </div>
      </modal-dialog>
    </main>
  </patient-profile>
</template>

<script>
import notifierMixin from '@/mixins/notifierMixin';
import { PatientQuestion, StoreQuestionnaireRequest } from '@/storage/gateways/PatientHealthQuestionnaireGateway';
import TextareaLengthLimitation from '@/components/TextareaLengthLimitation.vue';
import PatientProfile from '@/views/ClinicPanel/PatientProfile.vue';
import ModalDialogPreventLeave from '@/components/ModalDialogPreventLeave.vue';
import preventLeaveWithoutConfirmMixin from '@/mixins/preventLeaveWithoutConfirmMixin';
import debounce from 'lodash.debounce';
import SideSlideTransition from '@/components/SideSlideTransition.vue';
import ConfirmSideDialog from '@/components/ConfirmSideDialog.vue';
import FormPatientHealthQuestion from '@/components/FormPatientHealthQuestion.vue';
import CheckboxTextarea from '@/components/CheckboxTextarea.vue';
import ModalDialog from '@/components/ModalDialog.vue';
import draggable from 'vuedraggable';
import metricMixin from '@/mixins/metricMixin';
import {
  GOAL_ADD_HEALTH_QUESTIONNAIRE_QUESTIONS,
  GOAL_PRINT_HEALTH_QUESTIONNAIRE_QUESTIONS,
} from '@/plugins/metric';

const WAIT_TIME = 300;

/**
 * @description Доступ к активности "Удаление вопроса о здоровье".
 * @type {string}
 */
const DELETING_HEALTH_QUESTION = 'delete-question';

/**
 * @description Доступ к активности "Добавление вопроса о здоровье".
 * @type {string}
 */
const ADD_HEALTH_QUESTION = 'add-question';

/**
 * @description Доступ к активности "Редактирование вопроса о здоровье".
 * @type {string}
 */
const EDIT_HEALTH_QUESTION = 'edit-question';

export default {
  components: {
    ModalDialog,
    FormPatientHealthQuestion,
    ConfirmSideDialog,
    SideSlideTransition,
    ModalDialogPreventLeave,
    PatientProfile,
    TextareaLengthLimitation,
    CheckboxTextarea,
    draggable,
  },
  computed: {
    /**
     * @description Имеет право удалять вопрос.
     * @return {boolean}
     */
    isHasDeleteQuestion() {
      return this.actions.includes(DELETING_HEALTH_QUESTION);
    },
    /**
     * @description Имеет право добавлять вопрос.
     * @return {boolean}
     */
    isHasAddQuestion() {
      return this.actions.includes(ADD_HEALTH_QUESTION);
    },
    /**
     * @description Имеет право редактировать вопрос.
     * @return {boolean}
     */
    isHasEditQuestion() {
      return this.actions.includes(EDIT_HEALTH_QUESTION);
    },
    /**
     * @description Идентификатор пациента
     * @param vm контекст
     * @return {number}
     */
    patientId: (vm) => parseInt(vm.$route.params.id, 10),
    /**
     * @description Предотвращать уход со страницы.
     * @type {boolean}
     */
    needPreventLeave(vm) {
      return vm.isQuestionnaireChanged;
    },
  },
  created() {
    this.getQuestionnaire();
  },
  data() {
    return {
      /**
       * @description Максимальная длина вопроса.
       * @type {Number}
       */
      maxLengthQuestion: 254,
      /**
       * @description Доступные действия.
       * @type Array<string>
       */
      actions: [],
      selectedQuestionId: null,
      deleteDialog: false,
      questionnaire: {},
      isQuestionnaireStoring: false,
      isShownDialogLeaveConfirm: false,
      isQuestionnaireChanged: false,
      isShowCreateModal: false,
      editPatientHealthQuestion: null,
      createPatientHealthQuestion: null,
      inputErrors: {},
      /**
       * @description Ознакомлен с правилами удаления.
       * @type {boolean}
       */
      isAgreeWithRulesDelete: false,
    };
  },
  methods: {
    /**
     * @description Проверка прав на возможность перемещать вопросы
     *
     * @return {boolean}
     */
    checkMove() {
      return this.isHasAddQuestion;
    },
    getQuestionnaire() {
      const onSuccess = (questionnaire) => {
        this.questionnaire = questionnaire;
        this.actions = questionnaire.actions;
      };
      const onFail = (data) => {
        this.notifyFail(data.title);
      };
      this.$patientHealthQuestionnaireGateway.getQuestionnaire(this.patientId)
        .then((either) => {
          either.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Запрос на создание вопроса анкеты пациента
     *
     * @param patientQuestion {PatientQuestion}
     * @return {void}
     */
    onSubmitCreatePatientQuestion(patientQuestion) {
      const onSuccess = (data) => {
        this.getQuestionnaire();
        this.notifySuccess(data.data.message);
        this.isShowCreateModal = false;
      };
      const onFail = (error) => {
        this.notifyFail(error.title);
        const failurePairs = (error.causes || []).map((cause) => [cause.source, cause.details]);
        this.inputErrors = Object.fromEntries(failurePairs);
      };
      this
        .$patientHealthQuestionnaireGateway
        .createQuestion(patientQuestion)
        .then((either) => {
          either.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Запрос на редактирование вопроса анкеты пациента
     *
     * @param id
     * @param questionOld
     * @param groupId
     * @param isOpenQuestion
     * @param questionNew
     */
    onSubmitEditPatientQuestion(id, questionOld, groupId, isOpenQuestion, questionNew) {
      if (questionOld === questionNew) {
        return;
      }
      const patientQuestion = new PatientQuestion(
        id,
        groupId,
        questionNew,
        isOpenQuestion,
      );
      const onSuccess = (data) => {
        this.notifySuccess(data.message);
      };
      const onFail = (data) => {
        this.inputErrors = data.violations;
        this.notifyFail(data.title);
      };
      this
        .$patientHealthQuestionnaireGateway
        .editQuestion(patientQuestion, this.patientId)
        .then((either) => {
          either.fold(onFail, onSuccess);
        });
    },
    /**
     * @description Показать модальное окно создания вопроса для анкеты
     *
     * @returns {null}
     */
    onShowCreateModal() {
      this.createPatientHealthQuestion = new PatientQuestion(
        null,
        null,
        '',
        false,
      );
      this.isShowCreateModal = true;
      this.reachAddQuestionGoal();
    },
    /**
     * @description Достичь цели - добавление вопроса в анкете здоровья
     */
    reachAddQuestionGoal() {
      this.reachGoalIfNotReach(GOAL_ADD_HEALTH_QUESTIONNAIRE_QUESTIONS);
    },
    /**
     * Показать модальное окно удаления
     *
     * @param questionId
     *
     * @returns {void}
     */
    showDeleteModal(questionId) {
      this.selectedQuestionId = questionId;
      this.deleteDialog = true;
    },
    /**
     * Удалить вопрос
     *
     * @returns {void}
     */
    async onDeleteQuestion() {
      const onSuccess = (data) => {
        this.getQuestionnaire();
        this.notifySuccess(data.message);
        this.deleteDialog = false;
        this.isAgreeWithRulesDelete = false;
      };
      const onFail = (data) => {
        this.notifyFail(data.title);
        this.deleteDialog = false;
      };
      this
        .$patientHealthQuestionnaireGateway
        .deleteQuestion(this.patientId, this.selectedQuestionId)
        .then((either) => {
          either.fold(onFail, onSuccess);
        });
    },
    storeQuestionnaire() {
      const onSuccess = () => {
        this.isShownDialogLeaveConfirm = false;
        this.isQuestionnaireChanged = false;
        this.notifySuccess('Данные успешно обновлены');
      };
      const onFail = (data) => {
        this.notifyFail(data.title);
      };
      const request = new StoreQuestionnaireRequest(
        Object.entries(this.questionnaire.questions).map(([, { questions }]) => questions).flat(),
        this.patientId,
      );
      this.$patientHealthQuestionnaireGateway.storeQuestionnaire(request)
        .then((either) => {
          either.fold(onFail, onSuccess);
        });
    },
    async saveAndLeave() {
      this.storeQuestionnaire();
      this.isShownDialogLeaveConfirm = false;
      this.isQuestionnaireChanged = false;
    },
    /**
     * @description Распечатать анкету.
     * @returns {void}
     */
    printQuestionnaire() {
      const onSuccess = (response) => {
        const blob = new Blob([response], { type: 'text/html' });
        const container = this.$refs.printView;
        container.setAttribute('src', URL.createObjectURL(blob));
        container.onload = () => {
          container.contentWindow.print();
        };
        this.reachPrintQuestionnaire();
      };
      const onFail = (error) => {
        this.notifyFail(error.getTitle());
      };
      this.$patientGateway.viewQuestionnaireForPrint(this.patientId).then((result) => {
        result.fold(onFail, onSuccess);
      });
    },
    /**
     * @description Достигнута цель - печать вопросов о здоровье
     * @returns {void}
     */
    reachPrintQuestionnaire() {
      this.reachGoalIfNotReach(GOAL_PRINT_HEALTH_QUESTIONNAIRE_QUESTIONS);
    },
    /**
     * @description Удалить переносы строк в вопросе.
     * @param question
     * @returns {string}
     */
    removeLineBreaks(question) {
      return question.replace(/(\r\n|\n|\r)/gm, '');
    },
  },
  mixins: [notifierMixin, preventLeaveWithoutConfirmMixin, metricMixin],
  name: 'ClinicPanelPatientProfileHealthQuestionnaire',
  watch: {
    questionnaire: {
      handler: debounce(function update(value) {
        this.isQuestionnaireChanged = value.isChanged();
      }, WAIT_TIME),
      deep: true,
    },
  },
};
</script>
<style lang="scss" scoped>
@use "@/scss/buttons";

.patient-questionnaire-modal {
  &__controls {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    row-gap: 20px;
    margin: 0 auto;
    ::v-deep .v-input {
      margin: 0;
    }
  }
}
.modal-button {
  width: 100%;
  height: 2rem;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 135px;
  max-width: 180px;
  margin: 0 auto;
  border-radius: 4px;
  border: 1px solid #0489E0;
  background-color: #FFFFFF;
  color: #0489E0;
  transition: all 0.3s ease 0s;
  cursor: pointer;
  outline: none;
  &--remove {
    color: rgba(224, 4, 4, 0.97);
    border: 1px solid rgba(224, 4, 4, 0.97);
  }
  &:disabled {
    color:  gray;
    border: 1px solid gray;
    pointer-events: none;
  }
}

.patient-profile__questionnaire {
  font-family: "Roboto", sans-serif;
  &__header-buttons {
    display: flex;
    justify-content: flex-end;
    gap: 25px;
    margin: 20px 0 -35px auto;
    max-width: 300px;
  }
  &__print {
    display: none;
  }
  &__container {
    width: 100%;
    margin: 0 auto 0 0;
    padding: 38px 20px 48px 23px;
    position: relative;
  }
  &__form {
    margin: 10px 0 37px;
  }
  &__units {
    display: flex;
    flex-direction: column;
    gap: 60px;
    margin: 0 0 30px 0;
  }
  &__unit {
    display: flex;
    flex-direction: column;
    gap: 24px;
  }
  &__caption {
    font-weight: 600;
    font-size: 24px;
    line-height: 29px;
  }
  &__button {
    @include buttons.btn-blue(100%, 48px, 16px);
    max-width: 174px;
    &-print {
      @include buttons.btn-transparent(100%, 48px, 16px);
      max-width: 120px;
      padding: 0;
      z-index: 1;
      cursor: pointer;
      position: relative;
      &:before {
        content: "";
        background-image: url("@/assets/img/print.svg");
        background-repeat: no-repeat;
        background-size: contain;
        width: 24px;
        height: 24px;
        margin: 0 8px 0 0;
      }
      &:disabled {
        color: var(--unfocus-color);
      }
    }
    &-create-new {
      @include buttons.btn-blue(100%, 48px, 16px);
      max-width: 160px;
      padding: 0;
      z-index: 1;
      cursor: pointer;
      position: relative;
      &:disabled {
        color: var(--unfocus-color);
      }
    }
  }
  .patient-questions {
    display: grid;
    row-gap: 12px;
    margin: 0;
    padding: 0;
    &__item {
      position: relative;
      display: flex;
      flex-direction: column;
      gap: 5px;
      padding-block: 2px;
      &.ghost {
        background-color: #eaeff3;
        opacity: 0.7;
      }
    }
    &__question {
      display: flex;
      align-items: center;
      gap: 10px;
    }
    &__drag-handle {
      flex-shrink: 0;
      width: 20px;
      height: 22px;
      mask-image: url("@/assets/img/drag-indicator.svg");
      mask-repeat: no-repeat;
      mask-position: center center;
      mask-size: contain;
      background-color: #90A0B7;
      cursor: grab;
      &:hover {
        background-color: var(--action-color);
      }
      &:active {
        cursor: grabbing;
      }
    }
    &__button-del {
      display: block;
      width: 20px;
      height: 18px;
      mask: url("@/assets/img/cross.svg") no-repeat 50%;
      mask-size: 20px 18px;
      background-color: #90a0b7;
      transition: all 0.3s ease 0s;
      &:hover {
        background-color: #0489e0;
      }
    }
    &__description {
      width: 50%;
      padding-top: 4px;
      margin-left: 31px;
    }
  }
}

@media screen and (max-width: 786px) {
  .patient-profile__questionnaire {
    &__header-buttons {
      margin-bottom: 0;
    }
    .patient-questions {
      &__description {
        width: calc(100% - 54px);
      }
    }
  }
}

@media screen and (max-width: 480px) {
  .patient-profile__questionnaire {
    &__form {
      margin: 35px 0;
    }
    &__header-buttons {
      justify-content: flex-start;
      margin-left: 0;
    }
    &__units {
      row-gap: 50px;
    }
    &__button {
      &-print {
        display: none;
      }
    }
  }
}
</style>
